import { Component, inject, OnInit } from '@angular/core';
import { SecondaryPopupComponent } from '../../../../shared/components/secondary-popup/secondary-popup.component';
import { UserDetailsComponent } from '../user-details/user-details.component';
import { ManageUsersService } from '../../services/manage-users.service';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { UserDetails } from '../../../../shared/models/user';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { catchError, of, switchMap, takeUntil, tap } from 'rxjs';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterComponent } from '../../../../shared/components/filter/filter.component';
import { ManageConnectionService } from '../../../manage-connection-group/services/manage-connection.service';
import { SearchInputComponent } from '../../../../shared/components/search-input/search-input.component';
import {
  TableColumn,
  TableComponent,
} from '../../../../shared/components/table/table.component';
import { ContainerSasUrl } from '../../../../shared/interfaces/company';
import { ImageService } from '../../../../shared/services/image-service/image.service';

@Component({
  selector: 'app-users-list',
  standalone: true,
  imports: [
    SecondaryPopupComponent,
    UserDetailsComponent,
    PaginationComponent,
    DatePipe,
    FormsModule,
    PageSizeComponent,
    FilterComponent,
    SearchInputComponent,
    TableComponent,
  ],
  templateUrl: './users-list.component.html',
  styleUrl: './users-list.component.scss',
})
export class UsersListComponent extends DestroyBase implements OnInit {
  manageConnectionService = inject(ManageConnectionService);
  imageService = inject(ImageService);
  manageUsersService = inject(ManageUsersService);
  route = inject(ActivatedRoute);
  router = inject(Router);

  activeTab: 'organization' | 'sme' = 'organization';
  loading = true;
  showUserDetailsPopup = false;
  users: UserDetails[] = [];
  currentPage = 1;
  pageSize = 50;
  totalUsers = 0;
  selectedUser!: UserDetails;
  connectionColors: Record<string, string> = {};
  selectedConnections: string[] = [];
  searchText = '';
  filteredUsers: UserDetails[] = [];
  selectedTagColour!: string;
  filterBy = 'all';
  filterByOptions = [
    {
      option: 'All',
      value: 'all',
    },
  ];
  allConnections: string[] = [];
  tableColumns: TableColumn[] = [
    { header: 'Name' },
    { header: 'Email' },
    { header: 'Connection' },
    {
      header: 'Last Login',
      style: { color: '#6b7280', 'font-size': '0.875rem' },
    },
    { header: '' },
  ];

  containerSasUrl!: ContainerSasUrl;

  ngOnInit(): void {
    // Fetch all connections for filter options first, then handle query params
    this.manageConnectionService
      .getConnectionsFromDB()
      .pipe(
        catchError(() => of([])),
        takeUntil(this.destroy$),
        switchMap((connections) => {
          this.allConnections = connections.map((conn) => conn.connection_name);
          this.filterByOptions = [
            { option: 'All', value: 'all' },
            ...connections.map((conn) => ({
              option: conn.connection_display_name || conn.connection_name,
              value: conn.connection_name,
            })),
          ];
          return this.route.queryParams;
        }),
        switchMap((params) => {
          const tabParam = params['tab'];
          if (tabParam === 'sme') {
            this.activeTab = 'sme';
          } else {
            this.activeTab = 'organization';
          }

          const connectionParam = params['connection'];

          if (this.activeTab === 'organization' && connectionParam) {
            const matchedOption = this.filterByOptions.find(
              (opt) =>
                opt.value.toLowerCase() === connectionParam.toLowerCase() ||
                opt.option.toLowerCase() === connectionParam.toLowerCase(),
            );
            const selectedVal = matchedOption
              ? matchedOption.value
              : connectionParam;

            this.selectedConnections = [selectedVal];
            this.filterBy = selectedVal;
          } else {
            this.selectedConnections = [];
            this.filterBy = 'all';
          }

          return this.imageService.getContainerSasUrl();
        }),
        tap((sas) => {
          this.containerSasUrl = sas;
          this.loadUsers(this.currentPage, this.pageSize);
        }),
      )
      .subscribe();
  }

  getImageUrl(path: string): string {
    if (!this.containerSasUrl || !path) return '';
    return `${path}?${this.containerSasUrl.sas_token}`;
  }

  loadUsers(pageNumber: number, pageSize: number): void {
    if (this.selectedConnections.length > 0) {
      this.currentPage = 1;
      pageNumber = 1;
    }
    const isSme = this.activeTab === 'sme';
    this.manageUsersService
      .getAllUsers(
        pageNumber,
        pageSize,
        this.selectedConnections,
        this.searchText,
        false,
        isSme ? true : undefined,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.users = res.results;
        this.filteredUsers = this.users;
        this.totalUsers = res.count;
        this.assignColors();
        this.loading = false;
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadUsers(this.currentPage, this.pageSize);
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadUsers(this.currentPage, this.pageSize);
    }
  }

  viewUserDetails(user: UserDetails, color: string): void {
    this.selectedUser = user;
    this.selectedTagColour = color;
    this.showUserDetailsPopup = true;
  }

  assignColors() {
    const colors = [
      'rgba(242, 139, 130, 0.3)',
      'rgba(251, 188, 4, 0.3)',
      'rgba(52, 168, 83, 0.3)',
      'rgba(66, 133, 244, 0.3)',
      'rgba(161, 66, 244, 0.3)',
      'rgba(247, 135, 66, 0.3)',
      'rgba(66, 245, 227, 0.3)',
      'rgba(224, 255, 185, 0.3)',
    ];
    let colorIndex = 0;

    for (const user of this.users) {
      const connection = user.connection_display_name;
      if (!this.connectionColors[connection]) {
        this.connectionColors[connection] = colors[colorIndex % colors.length];
        colorIndex++;
      }
    }
  }

  onFilterByConnection(connection: string): void {
    this.router.navigate([], {
      queryParams: { connection: connection },
      queryParamsHandling: '',
    });
  }

  onRemoveConnectionFilter(connection: string): void {
    this.selectedConnections = this.selectedConnections.filter(
      (item) => item !== connection,
    );

    this.router.navigate([], {
      queryParams: { connection: null },
      queryParamsHandling: 'merge',
    });
  }

  onSearchInputChange(query?: string): void {
    if (query !== undefined) {
      this.searchText = query;
    }
    this.currentPage = 1;
    this.loadUsers(this.currentPage, this.pageSize);
  }

  onCloseUserDetailsPopup(isClickedOutside: boolean): void {
    if (!isClickedOutside) {
      this.showUserDetailsPopup = false;
    }
  }

  onFilterByChange(value: string): void {
    this.currentPage = 1;
    this.filterBy = value;
    const filterParam = this.filterBy === 'all' ? '' : this.filterBy;
    this.onFilterByConnection(filterParam);
  }

  onTabChange(tabId: 'organization' | 'sme'): void {
    if (this.activeTab !== tabId) {
      this.activeTab = tabId;
      this.currentPage = 1;
      this.searchText = '';
      this.loading = true;

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tabId, connection: null },
        queryParamsHandling: 'merge',
      });
    }
  }
}
